import { ORDER_STATUS } from 'types'

// --- Special actions (manager order show) -----------------------------------

export const ADD_TECH_COMMENTS_ACTION = 'add_tech_comments'
export const ADD_TECH_COMMENTS_LABEL = 'Add Tech Comments'

export const TECH_COMMENTS_REQUIRED_MESSAGE = 'Please enter tech comments'
export const TECH_COMMENTS_SAVED_MESSAGE =
  'The tech comments were saved successfully.'

// Shown when an action is rejected because the page was showing a stale work
// order (e.g. another manager or Salesforce already changed its status).
export const STALE_WORK_ORDER_MESSAGE =
  'This work order was updated elsewhere. It has been refreshed to show its current status.'

// Statuses in which a manager may add tech comments, regardless of sub status.
// An allowlist on purpose (rather than blocking the finished statuses) so a
// status the frontend has no constant for never gets the action by accident.
// Mirrors WorkOrder::TECH_COMMENTS_EDITABLE_STATUSES on the API.
export const TECH_COMMENTS_ALLOWED_STATUSES = [
  ORDER_STATUS.ASSIGNED,
  ORDER_STATUS.SCHEDULED,
  ORDER_STATUS.AWAITING_PARTS,
  ORDER_STATUS.ESTIMATE,
  ORDER_STATUS.TECH_ON_SITE,
]

// The special actions partials. At most one applies to a work order; see
// getSpecialActionsPartial in ./specialActions.
export const SPECIAL_ACTIONS_PARTIAL = {
  ACCEPT_DECLINE: 'accept_decline',
  RESCHEDULING: 'rescheduling',
  IN_PROGRESS: 'in_progress',
  ADD_TECH_COMMENTS: 'add_tech_comments',
}

// --- Dashboard ---------------------------------------------------------------

export const ORDER_TYPE_LABELS_BY_ORDER_TYPE_MAP = {
  field_service: 'Field Service',
  contracted_service: 'Contracted Service',
  non_dispatched_service: 'Non-Dispatched Service',
  logistics_service: 'Logistics Service',
  shop_service: 'Shop Service',
}

export const MANAGER_DASHBOARD_SECTIONS_CONFIG = [
  {
    id: 'awaiting-accept-decline',
    title: 'AWAITING ACCEPT/DECLINE',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'agingAsp',
        label: 'WO Aging (ASP)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'awaiting-accept-decline-pm',
    title: 'AWAITING ACCEPT/DECLINE (PM)',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'agingAsp',
        label: 'WO Aging (ASP)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'overdue-eta',
    title: 'OVERDUE ETA',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'agingAsp',
        label: 'WO Aging (ASP)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'scheduled-or-in-progress',
    title: 'SCHEDULED OR IN PROGRESS',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'etaDate',
        label: 'ETA Date',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
      {
        key: 'agingAsp',
        label: 'WO Aging (ASP)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'backorders-and-estimates',
    title: 'BACKORDERS AND ESTIMATES',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'orderStatusGrouped',
        label: 'Order Status - Grouped',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
      {
        key: 'agingAsp',
        label: 'WO Aging (ASP)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'orders-awaiting-confirmation',
    title: 'ORDERS AWAITING CONFIRMATION',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'workCompleteDateTime',
        label: 'Work Complete Date Time',
        fallback: 'Not Completed',
        link: false,
        isDateTime: true,
      },
      {
        key: 'agingAsp',
        label: 'WO Aging (ASP)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'awaiting-invoice',
    title: 'AWAITING INVOICE',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'workCompleteDateTime',
        label: 'Work Complete Date',
        fallback: 'Not Completed',
        link: false,
        isDateTime: true,
      },
      {
        key: 'agingRni',
        label: 'WO Aging (RNI)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'under-review',
    title: 'UNDER REVIEW',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'agingRni',
        label: 'WO Aging (RNI)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'billing-hold',
    title: 'BILLING HOLD',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'agingRni',
        label: 'WO Aging (RNI)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'shop-and-logistics',
    title: 'SHOP AND LOGISTICS',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'mostRecentAssignedDateTime',
        label: 'Most Recent Assigned Date Time',
        fallback: 'Pending Assignment',
        link: false,
        isDateTime: true,
      },
      {
        key: 'city',
        label: 'City',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'agingAsp',
        label: 'WO Aging (ASP)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
  {
    id: 'parts-order-po-number-needed',
    title: 'PARTS ORDER PO NUMBER NEEDED',
    columns: [
      {
        key: 'name',
        label: 'Work Order Number',
        textColor: '#DA251D',
        link: true,
        isDateTime: false,
      },
      {
        key: 'location',
        label: 'Location',
        textColor: '#DA251D',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'city',
        label: 'City',
        fallback: 'Unknown',
        link: false,
        isDateTime: false,
      },
      {
        key: 'agingAsp',
        label: 'WO Aging (ASP)',
        fallback: '—',
        link: false,
        isDateTime: false,
      },
    ],
  },
]

export const MANAGER_LIST_SECTION_DEFAULT_LIMIT = 5

export const MANAGER_DASHBOARD_DEFAULT_FILTER_VALUES = {
  list: '',
  location: '',
  city: '',
  orderType: '',
}

// Friendly labels for each manager work order list category (snake_case keys).
// Used by the index table to render the List column.
export const MANAGER_WORK_ORDER_LIST_LABELS = {
  awaiting_accept_decline: 'Awaiting Accept/Decline',
  awaiting_accept_decline_pm: 'Awaiting Accept/Decline (PM)',
  overdue_eta: 'Overdue ETA',
  backorders_and_estimates: 'Backorders and Estimates',
  scheduled_or_in_progress: 'Scheduled or In Progress',
  orders_awaiting_confirmation: 'Orders Awaiting Confirmation',
  awaiting_invoice: 'Awaiting Invoice',
  under_review: 'Under Review',
  billing_hold: 'Billing Hold',
  shop_and_logistics: 'Shop and Logistics',
  parts_order_po_number_needed: 'Parts Order PO Number Needed',
}

// Color tokens for the List column on the index table.
export const MANAGER_WORK_ORDER_LIST_COLORS = {
  awaiting_accept_decline: '#E59B0E',
  awaiting_accept_decline_pm: '#E59B0E',
  overdue_eta: '#DA251D',
  backorders_and_estimates: '#DA251D',
  scheduled_or_in_progress: '#E59B0E',
  orders_awaiting_confirmation: '#E59B0E',
  awaiting_invoice: '#E59B0E',
  under_review: '#3F6FB5',
  billing_hold: '#3F6FB5',
  shop_and_logistics: '#3F6FB5',
  parts_order_po_number_needed: '#DA251D',
}

// Friendly labels for raw work order `order_status` values. Used by the index
// table to render the Status column. Only terminal statuses have explicit labels
// today; other statuses fall through to the raw API value.
export const MANAGER_WORK_ORDER_STATUS_LABELS = {
  'Work Completed': 'Complete',
  Closed: 'Complete',
  Canceled: 'Canceled',
}

// Color tokens for the Status column on the index table, keyed by raw
// `order_status` value. Statuses without an entry render with the default
// table text color.
export const MANAGER_WORK_ORDER_STATUS_COLORS = {
  'Work Completed': '#1F8C3B',
  Closed: '#1F8C3B',
  Canceled: '#1F8C3B',
}

// Columns on the work orders index table. Each column is sortable per the
// `sortable` flag (defaults to true via WorkOrderTableSection's behavior).
export const MANAGER_INDEX_COLUMNS = [
  {
    key: 'date',
    label: 'Date',
    fallback: '—',
    link: false,
    isDateTime: false,
  },
  {
    key: 'name',
    label: 'ID',
    textColor: '#DA251D',
    link: true,
    isDateTime: false,
  },
  {
    key: 'location',
    label: 'Location',
    textColor: '#DA251D',
    fallback: 'Unknown',
    link: false,
    isDateTime: false,
  },
  {
    key: 'city',
    label: 'City',
    fallback: 'Unknown',
    link: false,
    isDateTime: false,
  },
  {
    key: 'orderType',
    label: 'Order Type',
    fallback: '—',
    link: false,
    isDateTime: false,
  },
  {
    key: 'list',
    label: 'List',
    fallback: '—',
    link: false,
    isDateTime: false,
  },
  {
    key: 'status',
    label: 'Status',
    fallback: '—',
    link: false,
    isDateTime: false,
  },
]

// Client-side page size for the work orders index table. The backend still
// returns every work order in a single request; this only controls how many
// are shown per page in the UI.
export const MANAGER_INDEX_PAGE_SIZE = 20
